import {
	useTranslationAdminDashboard,
	useTranslationOrganization,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useState } from 'react';
import { useParams, generatePath, useNavigate, Navigate } from 'react-router-dom';
import {
	organizationSettingsPath,
	parseOrganizationSettingsPath,
} from '@dealstack/frontend/app/pages/Organization/Organization.utils';
import { OrganizationGeneralSettings } from '@dealstack/frontend/app/pages/Organization/OrganizationGeneralSettings/OrganizationGeneralSettings';
import { DashboardGrid } from '@dealstack/frontend/shared/layout/DashboardGrid/DashboardGrid';
import { OrganizationSettingsSidebar } from '@dealstack/frontend/app/pages/Organization/OrganizationSettingsSidebar/OrganizationSettingsSidebar';
import { OrganizationSettingsView } from '@dealstack/frontend/app/pages/Organization/Organization.types';
import {
	addOrgPermissionToUser,
	deleteUserFromOrganization,
	removeOrgPermissionFromUser,
} from '@dealstack/frontend/shared/dataExchange/services/organization.api';
import { useMutation } from '@tanstack/react-query';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import { Flex, Text } from '@mantine/core';
import { OrganizationUsers } from '@dealstack/frontend/app/pages/Organization/OrganizationUsers/OrganizationUsers';
import { UserManagement } from '@dealstack/frontend/shared/components/UserManagement/UserManagement';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	FGAInternalRole,
	getResourceTypePermissionsWithOwner,
	PermissionRole,
	PermissionWithOwner,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { transformPermissions } from '@dealstack/frontend/shared/components/UserPermissions/userPermissions.utils';
import { useAppSelector } from '@dealstack/frontend/admin/hooks/redux';
import { selectUserId } from '@dealstack/frontend/app/redux/user.selectors';
import { useUserOrganizationId } from '@dealstack/frontend/shared/hooks/useUserOrganizationId/useUserOrganizationId.hook';
import { selectSelectedOrganizationUserId } from '@dealstack/frontend/app/pages/Organization/Organization.selectors';
import { useAppDispatch } from '@dealstack/frontend/app/hooks/redux';
import { setSelectedOrganizationUserId } from '@dealstack/frontend/app/pages/Organization/Organization.slice';
import { OrganizationAuthentication } from '@dealstack/frontend/app/pages/Organization/OrganizationAuthentication/OrganizationAuthentication';
import { OrganizationFunds } from '@dealstack/frontend/app/pages/Organization/OrganizationFunds/OrganizationFunds';
import { FundLinkedDeals } from '@dealstack/frontend/app/pages/Organization/OrganizationFunds/components/FundLinkedDeals/FundLinkedDeals';
import { FundWithDealRes, OrganizationType } from '@dealstack/shared/lib/generated';
import { useOrganizationUsers } from '@dealstack/frontend/app/pages/Organization/hooks/useOrganizationUsers';
import { useOrganizationUser } from '@dealstack/frontend/app/pages/Organization/hooks/useOrganizationUser';
import { useOrganization } from '@dealstack/frontend/app/pages/Organization/hooks/useOrganization';
import { useEmailTemplate } from '@dealstack/frontend/shared/components/EmailTemplates/hooks/useEmailTemplates';
import { EmailTemplateScopeKind } from '@dealstack/frontend/shared/components/EmailTemplates/EmailTemplates.types';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';
import { ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { ProgrammeDefaultsKycSettings } from '@dealstack/frontend/app/pages/Organization/OrganizationProgrammeDefaults/ProgrammeDefaultsKycSettings/ProgrammeDefaultsKycSettings';
import { ProgrammeDefaultsInvestorData } from '@dealstack/frontend/app/pages/Organization/OrganizationProgrammeDefaults/ProgrammeDefaultsInvestorData/ProgrammeDefaultsInvestorData';
import { ProgrammeDefaultsVestingTerms } from '@dealstack/frontend/app/pages/Organization/OrganizationProgrammeDefaults/ProgrammeDefaultsVestingTerms/ProgrammeDefaultsVestingTerms';
import { ProgrammeDefaultsMasterDocuments } from '@dealstack/frontend/app/pages/Organization/OrganizationProgrammeDefaults/ProgrammeDefaultsMasterDocuments/ProgrammeDefaultsMasterDocuments';
import { ProgrammeDefaultsTaxAndCompliance } from '@dealstack/frontend/app/pages/Organization/OrganizationProgrammeDefaults/ProgrammeDefaultsTaxAndCompliance/ProgrammeDefaultsTaxAndCompliance';
import { ProgrammeDefaultsEmailTemplates } from '@dealstack/frontend/app/pages/Organization/OrganizationProgrammeDefaults/ProgrammeDefaultsEmailTemplates/ProgrammeDefaultsEmailTemplates';
import { ProgrammeDefaultsEmailReminders } from '@dealstack/frontend/app/pages/Organization/OrganizationProgrammeDefaults/ProgrammeDefaultsEmailReminders/ProgrammeDefaultsEmailReminders';
import { ProgrammeDefaultsMembers } from '@dealstack/frontend/app/pages/Organization/OrganizationProgrammeDefaults/ProgrammeDefaultsMembers/ProgrammeDefaultsMembers';
import { DealDefaultsMembers } from '@dealstack/frontend/app/pages/Organization/OrganizationDealDefaults/DealDefaultsMembers/DealDefaultsMembers';
import { DealDefaultsClassifiers } from '@dealstack/frontend/app/pages/Organization/OrganizationDealDefaults/DealDefaultsClassifiers/DealDefaultsClassifiers';
import { DealDefaultsEmailTemplates } from '@dealstack/frontend/app/pages/Organization/OrganizationDealDefaults/DealDefaultsEmailTemplates/DealDefaultsEmailTemplates';
import { StyledOrganizationSettingsContainer } from '@dealstack/frontend/app/pages/Organization/styles';
import {
	isOrganizationSettingsSectionView,
	useOrganizationSettingsViewLabels,
} from '@dealstack/frontend/app/pages/Organization/hooks/useOrganizationSettingsViewLabel';

export const Organization = () => {
	const tOrganization = useTranslationOrganization();
	const tAdminDashboard = useTranslationAdminDashboard();
	const viewLabels = useOrganizationSettingsViewLabels();
	const currentUserId = useAppSelector(selectUserId);
	const selectedOrganizationMemberId = useAppSelector(selectSelectedOrganizationUserId);
	const dispatch = useAppDispatch();
	const [selectedFund, setSelectedFund] = useState<FundWithDealRes | null>(null);

	const { value: fundsEnabled } = useDealstackFeatureFlag(FEATURE_FLAG.FUNDS);
	const { hasPermission: hasSupportPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: '',
		permissionRole: FGAInternalRole.DEALSTACK_SUPPORT,
	});
	const enableFunds = fundsEnabled && hasSupportPermission;

	const { organizationId: organizationIdFromRoute, '*': splat } = useParams();
	const { organizationId: userOrganizationId } = useUserOrganizationId();
	const organizationId = organizationIdFromRoute ?? userOrganizationId;
	const navigate = useNavigate();

	const { view: currentView, id: viewId } = parseOrganizationSettingsPath(splat);
	// Only the User view carries a member id; other views put their own record in that segment.
	const selectedMemberId =
		currentView === OrganizationSettingsView.User
			? viewId ?? selectedOrganizationMemberId
			: selectedOrganizationMemberId;

	const isDealTemplateDetail =
		currentView === OrganizationSettingsView.DealDefaultsEmailTemplates && !!viewId;
	const isEmailTemplateDetail =
		(currentView === OrganizationSettingsView.ProgrammeDefaultsEmailTemplates && !!viewId) ||
		isDealTemplateDetail;
	const { data: openEmailTemplate } = useEmailTemplate({
		scope: {
			kind: isDealTemplateDetail
				? EmailTemplateScopeKind.ORGANIZATION_DEAL
				: EmailTemplateScopeKind.ORGANIZATION,
			id: organizationId ?? '',
		},
		type: viewId ?? '',
		enabled: isEmailTemplateDetail && !!organizationId,
	});

	const setCurrentView = (view: OrganizationSettingsView, id?: string) => {
		navigate(organizationSettingsPath(defaultToEmptyString(organizationId), view, id));
	};

	const selectFundAndView = (view: OrganizationSettingsView, fund: FundWithDealRes) => {
		setSelectedFund(fund);
		setCurrentView(view, fund.id);
	};

	const selectUserAndView = (view: OrganizationSettingsView, userId: string) => {
		dispatch(setSelectedOrganizationUserId(userId));
		setCurrentView(view, userId);
	};
	const { data: organization, isFetching: isFetchingOrganization } =
		useOrganization(organizationId);

	const { value: programmeDefaultsEnabled } = useDealstackFeatureFlag(
		FEATURE_FLAG.PROGRAMME_DEFAULTS
	);
	const { hasPermission: hasOrganizationAdminPermission } = usePermissionRequest({
		resourceType: ResourceType.ORGANIZATION,
		resourceId: organizationId ?? '',
		permissionRole: PermissionRole.ADMIN,
	});
	// Only Sponsor organisations can define Programme Defaults.
	const enableProgrammeDefaults =
		programmeDefaultsEnabled && organization?.type === OrganizationType.SPONSOR;
	const { value: enableEmailReminders } = useDealstackFeatureFlag(
		FEATURE_FLAG.AUTOMATED_EMAIL_REMINDERS
	);

	const { refetch: refetchOrganizationUsers } = useOrganizationUsers({
		organizationId: organization?.id ?? '',
		enabled: false,
	});

	const { mutate: deleteUserFromOrgMutation, isPending: isDeletingUserFromOrg } = useMutation({
		mutationFn: deleteUserFromOrganization,
	});
	const removeUserFromOrg = (userId: string) => {
		if (!organization?.id) {
			return;
		}

		deleteUserFromOrgMutation(
			{ userId, organizationId: organization.id },
			{
				onSuccess: async () => {
					showToastV2({
						message: tAdminDashboard(
							'programme_settings.member.toast.remove_user.success_message'
						),
					});
					setCurrentView(OrganizationSettingsView.Members);
					await refetchOrganizationUsers();
				},
			}
		);
	};

	const {
		data: user,
		isFetching: isFetchingUser,
		refetch: refetchUser,
	} = useOrganizationUser({
		userId: selectedMemberId!,
		organizationId: organization?.id ?? '',
		enabled: !!selectedMemberId && !!organization?.id,
		select: (user) => {
			// Get organization permissions dynamically
			const organizationPermissions = getResourceTypePermissionsWithOwner(
				ResourceType.ORGANIZATION
			);
			const resourcePermissions = organizationPermissions.map((permission) => ({
				label: tOrganization(`invite_team_member.relation.${permission}`),
				value: permission,
				description: tOrganization(`invite_team_member.relation.description.${permission}`),
			}));

			const isCurrentUser = currentUserId === selectedMemberId;
			const permissions = transformPermissions({
				resourcePermissions,
				userPermissions: user.permissions as unknown as PermissionWithOwner[],
				disablePermissionForCurrentUser: isCurrentUser,
				resourceType: ResourceType.ORGANIZATION,
			});
			return {
				...user,
				permissions,
			};
		},
	});

	const { mutate: addOrgPermissionToUserMutation, isPending: isAddingOrgPermissionToUser } =
		useMutation({
			mutationFn: addOrgPermissionToUser,
		});
	const {
		mutate: removeOrgPermissionFromUserMutation,
		isPending: isRemovingOrgPermissionFromUser,
	} = useMutation({
		mutationFn: removeOrgPermissionFromUser,
	});

	const updatePermission = (permissionRole: PermissionRole, removePermission: boolean) => {
		const onSuccess = async () => {
			showToastV2({
				message: tAdminDashboard('toast.update_user_permission.success_message'),
			});
			await refetchUser();
			await refetchOrganizationUsers();
		};

		if (removePermission) {
			return removeOrgPermissionFromUserMutation(
				{
					organizationId: organization?.id ?? '',
					userId: selectedMemberId!,
					permissionRoles: [permissionRole],
				},
				{ onSuccess }
			);
		}
		return addOrgPermissionToUserMutation(
			{
				organizationId: organization?.id ?? '',
				userId: selectedMemberId!,
				permissionRoles: [permissionRole],
			},
			{ onSuccess }
		);
	};

	const parentCrumb = hasSupportPermission
		? {
				name: tOrganization('organizations_management.title'),
				url: ROUTES.ORGANIZATIONS,
				hideInAppHeader: true,
			}
		: null;
	const currentCrumb = {
		name: organization?.name ?? tOrganization('organization_settings.title'),
		url: generatePath(ROUTES.ORGANIZATION_BY_ID, {
			organizationId: defaultToEmptyString(organizationId),
		}),
		hideInAppHeader: true,
	};
	const sectionCrumb = isOrganizationSettingsSectionView(currentView)
		? {
				name: viewLabels[currentView],
				url: organizationSettingsPath(defaultToEmptyString(organizationId), currentView),
				hideInAppHeader: true,
			}
		: null;

	const openTemplateCrumb = openEmailTemplate
		? { name: openEmailTemplate.name, url: '', noLink: true, hideInAppHeader: true }
		: null;

	useBreadcrumbs(
		[parentCrumb, currentCrumb, sectionCrumb, openTemplateCrumb].filter(
			(crumb) => crumb !== null
		)
	);

	if (isFetchingOrganization) {
		return <FullscreenLoader name="pageLoading" />;
	}

	if (!organization) {
		return (
			<Flex gap="1rem">
				<Text size="B6Bold" c="dark.6">
					{tOrganization('create_organization_request.request')}
				</Text>
				<a href="mailto:support@dealstack.io">
					<Text size="B6Bold" c="blue.6">
						{tOrganization('create_organization_request.contact_support')}
					</Text>
				</a>
			</Flex>
		);
	}

	const renderContent = () => {
		switch (currentView) {
			case OrganizationSettingsView.General:
				return (
					<OrganizationGeneralSettings
						organization={organization}
						onViewChange={setCurrentView}
					/>
				);
			case OrganizationSettingsView.Members:
				return (
					<OrganizationUsers
						organization={organization}
						onViewChange={selectUserAndView}
					/>
				);
			case OrganizationSettingsView.Authentication:
				return (
					<OrganizationAuthentication
						name={organization.name}
						auth2FAEnabled={organization.auth2FAEnabled}
						ssoEnabled={organization.ssoEnabled ?? false}
						emailDomains={organization.emailDomains ?? []}
					/>
				);
			case OrganizationSettingsView.Funds:
				return enableFunds ? (
					<OrganizationFunds
						organization={organization}
						onViewChange={selectFundAndView}
					/>
				) : null;
			case OrganizationSettingsView.ProgrammeDefaults:
			case OrganizationSettingsView.ProgrammeDefaultsMembers:
				return enableProgrammeDefaults ? (
					<ProgrammeDefaultsMembers organizationId={organization.id ?? ''} />
				) : null;
			case OrganizationSettingsView.ProgrammeDefaultsKycSettings:
				return enableProgrammeDefaults ? (
					<ProgrammeDefaultsKycSettings
						organizationId={organization.id ?? ''}
						canEdit={hasOrganizationAdminPermission}
					/>
				) : null;
			case OrganizationSettingsView.ProgrammeDefaultsInvestorData:
				return enableProgrammeDefaults ? (
					<ProgrammeDefaultsInvestorData
						organizationId={organization.id ?? ''}
						canEdit={hasOrganizationAdminPermission}
					/>
				) : null;
			case OrganizationSettingsView.ProgrammeDefaultsVestingTerms:
				return enableProgrammeDefaults ? (
					<ProgrammeDefaultsVestingTerms organizationId={organization.id ?? ''} />
				) : null;
			case OrganizationSettingsView.ProgrammeDefaultsMasterDocuments:
				return enableProgrammeDefaults ? (
					<ProgrammeDefaultsMasterDocuments organizationId={organization.id ?? ''} />
				) : null;
			case OrganizationSettingsView.ProgrammeDefaultsTaxAndCompliance:
				return enableProgrammeDefaults ? (
					<ProgrammeDefaultsTaxAndCompliance organizationId={organization.id ?? ''} />
				) : null;
			case OrganizationSettingsView.ProgrammeDefaultsEmailTemplates:
				return enableProgrammeDefaults ? (
					<ProgrammeDefaultsEmailTemplates
						organizationId={organization.id ?? ''}
						templateType={viewId}
						onSelectTemplate={(type) =>
							setCurrentView(
								OrganizationSettingsView.ProgrammeDefaultsEmailTemplates,
								type ?? undefined
							)
						}
					/>
				) : null;
			case OrganizationSettingsView.ProgrammeDefaultsEmailReminders:
				return enableProgrammeDefaults && enableEmailReminders ? (
					<ProgrammeDefaultsEmailReminders organizationId={organization.id ?? ''} />
				) : null;
			case OrganizationSettingsView.DealDefaults:
			case OrganizationSettingsView.DealDefaultsMembers:
				return enableProgrammeDefaults ? (
					<DealDefaultsMembers organizationId={organization.id ?? ''} />
				) : null;
			case OrganizationSettingsView.DealDefaultsClassifiers:
				return enableProgrammeDefaults ? (
					<DealDefaultsClassifiers organizationId={organization.id ?? ''} />
				) : null;
			case OrganizationSettingsView.DealDefaultsEmailTemplates:
				return enableProgrammeDefaults ? (
					<DealDefaultsEmailTemplates
						organizationId={organization.id ?? ''}
						templateType={viewId}
						onSelectTemplate={(type) =>
							setCurrentView(
								OrganizationSettingsView.DealDefaultsEmailTemplates,
								type ?? undefined
							)
						}
					/>
				) : null;
			case OrganizationSettingsView.FundDeals:
				return selectedFund && selectedFund.id === viewId ? (
					<FundLinkedDeals
						fundId={selectedFund.id}
						fundName={selectedFund.deal.name}
						dealId={selectedFund.dealId}
					/>
				) : (
					<Navigate
						to={organizationSettingsPath(
							defaultToEmptyString(organizationId),
							OrganizationSettingsView.Funds
						)}
						replace
					/>
				);
			case OrganizationSettingsView.User:
				return (
					<UserManagement
						title={organization?.name || ''}
						deleteFromText={organization.name}
						user={user}
						membersBreadcrumbTitle={tOrganization(
							'organization_settings.labels.organization_members'
						)}
						isLoading={
							isFetchingUser ||
							isAddingOrgPermissionToUser ||
							isRemovingOrgPermissionFromUser ||
							isDeletingUserFromOrg
						}
						// TODO: Will implement this functionality in the future
						handleUpdateUser={() => Promise.resolve()}
						handlePermissionChange={updatePermission}
						handleRemoveUser={removeUserFromOrg}
						canRemoveUser={true}
						text={{
							permissionTitle: tOrganization(
								'invite_member_drawer.form.role_field.label'
							),
						}}
					/>
				);
			default:
				return (
					<OrganizationGeneralSettings
						organization={organization}
						onViewChange={setCurrentView}
					/>
				);
		}
	};

	return (
		<StyledOrganizationSettingsContainer>
			<DashboardGrid
				pageTitle={tOrganization('organization_settings.title')}
				showQuickSwitch={false}
				sidebarContent={
					<OrganizationSettingsSidebar
						title={organization.name}
						currentView={currentView}
						onViewChange={setCurrentView}
						enableFunds={enableFunds}
						enableProgrammeDefaults={enableProgrammeDefaults}
						enableEmailReminders={enableEmailReminders}
					/>
				}
				mainContent={renderContent()}
			/>
		</StyledOrganizationSettingsContainer>
	);
};
